import { AgmMap } from '@agm/core';
import {
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { filter, finalize, take } from 'rxjs/operators';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { ChannelStatus } from 'src/app/models/channel';
import { LoadingOverlayService } from 'src/app/shared/loading-overlay/loading-overlay.service';
import {
    getChannelSupplierStatus,
    showChannelStatusDistinction,
    checkIfModeExistsInQueryParams,
    isCountryBlockedForBilling
} from 'src/app/shared/utility/campaign';
import { approvedChannelStatuses } from 'src/app/models/channel';
import { Campaign, CampaignStatus } from '../../../models/campaign';
import { marker } from '../../../models/marker';
import { CampaignHelperService } from '../../../services/campaign-helper.service';
import { CampaignService } from '../../../services/campaign.service';
import { UtilsService } from '../../../services/utils.service';
import { AlertService } from '../../../shared/alert/alert.service';
import { CanComponentDeactivate } from '../guards/can-deactivate.interface';
import { CHANNEL_MAP_STYLES } from './map-styles';
import { calculateZoomForBounds, clampZoomForMarkerCount } from './map-utils';

declare var google: any;

@Component({
    selector: 'app-channels-location',
    templateUrl: './channels-location.component.html',
    styleUrls: ['./channels-location.component.scss']
})
export class ChannelsLocationComponent implements OnInit, CanComponentDeactivate {
    public form: FormGroup;
    public selectedCountry: any = {};
    public selectedCities: any[] = [];
    // google maps zoom level
    public zoom: number = 9;

    // initial center position for the map
    public lat: number = 25.094305;
    public lng: number = 55.238521;

    public markers: marker[] = [];

    campaignId;
    locations: any = { countries: [] };
    selectedChannels = new Set();
    selectedChannelsLocation = { countries: new Map() };
    isSnazzyInfoWindowOpened: boolean = false;
    campaign: Campaign;
    previewMode = false;
    partialEditMode = false;
    shouldDisableAllPointers = false;
    liveEditMode = false;
    countriesCoords;
    allAvailableChannels = [];
    private pendingOps$ = new BehaviorSubject<number>(0);
    toBeRemovedChannels = [];
    approvedChannelStatuses = approvedChannelStatuses;
    ChannelStatus = ChannelStatus;
    CampaignStatus = CampaignStatus;
    unfilteredallAvailableChannels = [];
    metrics: any;
    @ViewChild('AgmMap') agmMap: AgmMap;
    private map: google.maps.Map;

    mapStyles = CHANNEL_MAP_STYLES;
    markerClustererImagePath = './assets/images/map-cluster';
    infoMap = new Map<any, any>();

    public constructor(
        private loadingOverlayService: LoadingOverlayService,
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        private campaignService: CampaignService,
        private authenticationService: AuthenticationService,
        private router: Router,
        private utilsService: UtilsService,
        private campaignHelperService: CampaignHelperService,
        private alertService: AlertService,
        @Inject(ChangeDetectorRef) private changeDetectorRef: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        this.previewMode = checkIfModeExistsInQueryParams(this.route, 'preview');
        this.partialEditMode = checkIfModeExistsInQueryParams(this.route, 'partial-edit-mode');
        this.liveEditMode = checkIfModeExistsInQueryParams(this.route, 'live-edit-mode');
        this.shouldDisableAllPointers = [this.liveEditMode, this.previewMode].some(Boolean);
        this.initForm();
        this.getLocationsAndCampaign();
        this.getCountriesCoordinatesMap();
    }

    canDeactivate(): boolean {
        return this.previewMode;
    }

    async saveDraft(): Promise<boolean> {
        this.alertService.clear();
        if (!this.campaign || !this.campaign._id) {
            this.alertService.error('Campaign not found. Please try again.', {
                id: 'save-campaign',
                autoClose: true
            });
            return false;
        }
        // Channels are persisted immediately via addChannel/removeChannel endpoints.
        // Wait for any in-flight call so canDeactivate doesn't unmount mid-write.
        await this.waitForPendingOps();
        return true;
    }

    private trackOp<T>(obs: Observable<T>): Observable<T> {
        this.pendingOps$.next(this.pendingOps$.value + 1);
        return obs.pipe(finalize(() => this.pendingOps$.next(this.pendingOps$.value - 1)));
    }

    private waitForPendingOps(): Promise<void> {
        return this.pendingOps$
            .pipe(filter(count => count === 0), take(1))
            .toPromise()
            .then(() => undefined);
    }

    showChannelStatusDistinction() {
        return showChannelStatusDistinction(
            this.campaign.status,
            this.authenticationService.getSessionRole()
        );
    }

    get canEditApproved(): boolean {
        const previousState = this.campaign?.previousCampaignState;

        return (
            this.campaign?.status === 'BEING_EDITED' &&
            previousState?.status === 'PAUSED' &&
            ['APPROVED', 'PARTIALLY_APPROVED'].includes(previousState?.relaunchStatus)
        );
    }

    getLocationsAndCampaign() {
        this.campaignId = this.route.snapshot.paramMap.get('id');
        if (this.campaignId != null) {
            forkJoin(
                this.campaignService.getById(this.campaignId),
                this.campaignHelperService.getCampaignAvailableLocations(this.campaignId)
            ).subscribe(([campaignRes, locationsRes]) => {
                this.campaign = campaignRes;
                this.selectedChannels =
                    campaignRes.channels == null ? new Set() : new Set(campaignRes.channels);
                this.locations = locationsRes;
                this.locations = {
                    ...this.locations,
                    countries: this.locations.countries.map(country => ({
                        ...country,
                        cities: country.cities.map(city => ({
                            ...city,
                            zones: city.zones.map(zone => ({
                                ...zone,
                                channels: zone.channels.map(channel => ({
                                    ...channel,
                                    // Back-compat for the detail card/template: the old `groups` array
                                    // was dropped in the channel restructure. The backend now synthesizes
                                    // it on ChannelDTO; fall back to building it locally if absent.
                                    groups: (channel.groups && channel.groups.length)
                                        ? channel.groups
                                        : this.buildChannelGroups(channel)
                                }))
                            }))
                        }))
                    }))
                };
                this.disableFieldsForPublishedCampaign();
                this.createChannelsByCountryStructure();
                if (this.toBeRemovedChannels.length > 0) this.checkForChannelsToBeRemoved();
                // Metrics on load are fetched by <app-highlights> itself; add/remove responses
                // embed fresh metrics that flow back in via the [metrics] binding.
            });
        }
    }

    checkForChannelsToBeRemoved() {
        return this.toBeRemovedChannels.forEach(toBeRemovedChannel => {
            this.onRemoveChannel(
                toBeRemovedChannel.toBeRemovedChannelId,
                toBeRemovedChannel.countryId,
                toBeRemovedChannel.cityId
            );
        });
    }

    getCountriesCoordinatesMap() {
        this.utilsService.getCountriesCoordinates().subscribe(resp => {
            this.countriesCoords = resp;
        });
    }

    disableFieldsForPublishedCampaign() {
        ['country', 'city', 'indoor', 'outdoor'].forEach(name => {
            const control = this.form.get(name);
            if (this.shouldDisableAllPointers) {
                control.disable();
            } else {
                control.enable();
            }
        });
    }

    private initForm(): void {
        this.form = this.formBuilder.group({
            country: new FormControl(null),
            city: new FormControl([]),
            indoor: new FormControl(false),
            outdoor: new FormControl(false)
        });
    }

    get f() {
        return this.form.controls;
    }

    createChannelsByCountryStructure() {
        this.selectedChannelsLocation.countries = new Map();
        this.allAvailableChannels = [];
        this.locations.countries.forEach(country => {
            const countryKey = country.idcountry;
            const cityMap = new Map();
            const cityMapAll = new Map();

            country.cities.forEach(city => {
                const { selectedChannels, allChannels } = this.processCityChannels(city, country);

                if (selectedChannels.length > 0) {
                    cityMap.set(city.idcity, {
                        name: city.name,
                        channels: selectedChannels
                    });
                }

                cityMapAll.set(city.idcity, {
                    name: city.name,
                    channels: allChannels
                });
            });

            if (cityMap.size > 0) {
                this.selectedChannelsLocation.countries.set(countryKey, {
                    name: country.name,
                    cities: cityMap
                });
            }

            this.infoMap.set(countryKey, {
                name: country.name,
                cities: cityMapAll
            });
        });
    }

    processCityChannels(city, country) {
        const selectedChannels = [];
        const allChannels = [];

        // A country CPM cannot be booked in contributes no channels at all, so it drops out of the
        // tree rather than appearing with an empty city list.
        if (this.campaign && isCountryBlockedForBilling(country, this.campaign.billingMethod)) {
            return { selectedChannels, allChannels };
        }

        city.zones.forEach(zone => {
            zone.channels.forEach(channel => {
                let markedChannel;
                const channelStatus = getChannelSupplierStatus(this.campaign, channel.idChannel);
                const channelInfo = {
                    id: channel.idChannel,
                    name: channel.name,
                    status: channelStatus
                };

                allChannels.push(channelInfo);
                channel.countryKey = country.idcountry;
                channel.cityKey = city.idcity;
                channel.status = channelStatus;
                this.allAvailableChannels.push(channel);

                markedChannel = this.markers?.some(marker => {
                    return marker.options.idChannel == channel.idChannel;
                });

                if (this.selectedChannels?.has(channel.idChannel) && !markedChannel) {
                    channel.pricePerHour = channel.pricePerHour;
                    channel.image = channel.image;

                    selectedChannels.push(channelInfo);
                    this.markers.push({
                        lat: channel.lat,
                        lng: channel.lon,
                        label: channel.name,
                        options: channel,
                        draggable: false
                    });
                }
            });
        });

        return { selectedChannels, allChannels };
    }

    public getLocationByChannel(searchValue) {
        let foundChannel = false;
        let foundValue = null;
        for (let [keyCountry, valueCountry] of this.infoMap.entries()) {
            for (let [keyCity, valueCity] of valueCountry.cities.entries()) {
                for (let channel of valueCity.channels) {
                    if (channel.id === searchValue) {
                        foundValue = {
                            city: keyCity,
                            country: keyCountry,
                            countryName: valueCountry.name,
                            cityName: valueCity.name
                        };
                        foundChannel = true;
                        break;
                    }
                }
                if (foundChannel) break;
            }
            if (foundChannel) break;
        }
        return foundValue;
    }

    onChannelAdd(idChannel: any, channelName: string) {
        if (this.isChannelSelected(idChannel) || this.isReadOnlyFlow()) {
            return;
        }
        // A deactivated channel is only in this list because the campaign already booked it. It can
        // stay, but it can never be booked again.
        if (this.isChannelDeactivated(idChannel)) {
            return;
        }
        const channelStatus = getChannelSupplierStatus(this.campaign, idChannel);

        // Optimistic local UI update so the map/list reflect the click immediately.
        this.selectedChannels.add(idChannel);
        const channelsOnScreen = this.getLocationByChannel(idChannel);
        const countryKey = channelsOnScreen?.country || this.selectedCountry.idcountry;
        const cityKey = channelsOnScreen?.city || this.selectedCities[0]?.idcity;
        const location = this.infoMap.get(countryKey);
        const city = this.infoMap.get(countryKey).cities.get(cityKey);
        const newChannel = { id: idChannel, name: channelName, status: channelStatus };

        let countryEntry = this.selectedChannelsLocation.countries.get(countryKey);
        if (!countryEntry) {
            countryEntry = { name: location.name, cities: new Map() };
            this.selectedChannelsLocation.countries.set(countryKey, countryEntry);
        }
        let cityEntry = countryEntry.cities.get(cityKey);
        if (!cityEntry) {
            cityEntry = { name: city.name, channels: [] };
            countryEntry.cities.set(cityKey, cityEntry);
        }
        cityEntry.channels.push(newChannel);
        this.isSnazzyInfoWindowOpened = false;

        this.trackOp(this.campaignService.addChannel(this.campaign._id, idChannel)).subscribe({
            next: data => this.applyCampaignResponse(data),
            error: error => this.handleChannelOpError(error)
        });
    }

    onRemoveChannel(channelKey, country?, city?) {
        if (!this.isChannelSelected(channelKey) || this.shouldDisableAllPointers) {
            return;
        }
        const countryId = country ?? this.getLocationByChannel(channelKey)?.country;
        const cityId = city ?? this.getLocationByChannel(channelKey)?.city;

        if (!countryId || !cityId) {
            console.error('Cannot remove channel: country or city not found', {
                country,
                city,
                channelKey
            });
            return;
        }

        this.removeChannelLocally(countryId, cityId, channelKey);
        this.persistChannelRemoval(channelKey);
    }

    private removeChannelLocally(countryId, cityId, channelKey) {
        this.selectedChannels.delete(channelKey);

        const countryEntry = this.selectedChannelsLocation.countries.get(countryId);
        if (countryEntry) {
            const cityMap = countryEntry.cities;
            const cityEntry = cityMap.get(cityId);
            if (cityEntry) {
                const channels = cityEntry.channels.filter(ch => ch.id !== channelKey);
                if (channels.length === 0) {
                    cityMap.delete(cityId);
                    if (cityMap.size === 0) {
                        this.selectedChannelsLocation.countries?.delete(countryId);
                    }
                } else {
                    cityMap.set(cityId, { ...cityEntry, channels });
                }
            }
        }

        this.selectedChannelsLocation = {
            ...this.selectedChannelsLocation,
            countries: new Map(this.selectedChannelsLocation.countries)
        };
    }

    private persistChannelRemoval(channelKey) {
        const wasDeactivated = this.isChannelDeactivated(channelKey);

        this.trackOp(this.campaignService.removeChannel(this.campaign._id, channelKey)).subscribe({
            next: data => {
                this.applyCampaignResponse(data);
                if (wasDeactivated) {
                    // Removing it gives up the booking that was keeping it visible, so drop it from
                    // the local dataset too — the backend stops offering it on the next load.
                    this.purgeChannelFromDataset(channelKey);
                    this.changeDetectorRef.detectChanges();
                }
            },
            error: error => this.handleChannelOpError(error)
        });
    }

    /** True for a channel the admin has deactivated (directly or by deactivating its supplier). */
    isChannelDeactivated(idChannel: any): boolean {
        return !!this.allAvailableChannels?.find(ch => ch.idChannel === idChannel)?.disabled;
    }

    /** Drops a channel from the map, search and location lists so it can no longer be found. */
    private purgeChannelFromDataset(channelKey): void {
        this.locations = {
            ...this.locations,
            countries: this.locations.countries.map(country => ({
                ...country,
                cities: country.cities.map(city => ({
                    ...city,
                    zones: city.zones.map(zone => ({
                        ...zone,
                        channels: zone.channels.filter(ch => ch.idChannel !== channelKey)
                    }))
                }))
            }))
        };

        this.markers = this.markers.filter(m => m.options.idChannel !== channelKey);
        this.unfilteredallAvailableChannels = this.unfilteredallAvailableChannels.filter(
            ch => ch.idChannel !== channelKey
        );

        this.createChannelsByCountryStructure();
    }

    /** Shared success path for addChannel/removeChannel — backend embeds fresh metrics in the response. */
    private applyCampaignResponse(data: Campaign): void {
        this.campaign = data;
        if ((data as any).metrics) {
            this.metrics = (data as any).metrics;
        }
        // The backend auto-includes/removes linked channel siblings, so re-sync the selection
        // from the server's channel list and rebuild the visible "selected" list accordingly.
        this.selectedChannels = data.channels == null ? new Set() : new Set(data.channels);
        this.rebuildSelectedChannelsLocation();
        this.changeDetectorRef.detectChanges();
    }

    /**
     * Rebuilds the visible "selected channels" grouping purely from {@link selectedChannels},
     * {@link allAvailableChannels} and {@link infoMap}. Does not touch map markers (their highlight
     * is derived from selectedChannels via getMarkerIcon), so it is safe to call after every op.
     */
    private rebuildSelectedChannelsLocation(): void {
        const countries = new Map<any, any>();
        for (const channel of this.allAvailableChannels) {
            if (!this.selectedChannels.has(channel.idChannel)) continue;
            const countryKey = channel.countryKey;
            const cityKey = channel.cityKey;
            const info = this.infoMap.get(countryKey);
            if (!info) continue;
            const cityInfo = info.cities.get(cityKey);

            let countryEntry = countries.get(countryKey);
            if (!countryEntry) {
                countryEntry = { name: info.name, cities: new Map() };
                countries.set(countryKey, countryEntry);
            }
            let cityEntry = countryEntry.cities.get(cityKey);
            if (!cityEntry) {
                cityEntry = { name: cityInfo?.name, channels: [] };
                countryEntry.cities.set(cityKey, cityEntry);
            }
            if (!cityEntry.channels.some((c: any) => c.id === channel.idChannel)) {
                cityEntry.channels.push({
                    id: channel.idChannel,
                    name: channel.name,
                    status: getChannelSupplierStatus(this.campaign, channel.idChannel)
                });
            }
        }
        this.selectedChannelsLocation = { ...this.selectedChannelsLocation, countries };
    }

    /**
     * Returns the names of linked-channel siblings that are missing from the current selection.
     * Linked channels must be booked together, so the campaign cannot proceed while any are missing.
     */
    private getMissingLinkedSiblings(): string[] {
        const byGroup = new Map<number, any[]>();
        for (const ch of this.allAvailableChannels) {
            const groupId = (ch as any).linkGroupId;
            if (groupId == null) continue;
            const members = byGroup.get(groupId) ?? [];
            members.push(ch);
            byGroup.set(groupId, members);
        }
        const missing: string[] = [];
        byGroup.forEach(members => {
            const anySelected = members.some(m => this.selectedChannels.has(m.idChannel));
            if (!anySelected) return;
            members.forEach(m => {
                if (!this.selectedChannels.has(m.idChannel)) missing.push(m.name);
            });
        });
        return missing;
    }

    private handleChannelOpError(error: any): void {
        this.alertService.error('Error: ' + error, {
            id: 'save-campaign',
            autoClose: true
        });
        this.changeDetectorRef.detectChanges();
    }

    isChannelSelected(idChannel) {
        return this.selectedChannels.has(idChannel);
    }

    onChangeCountry(country) {
        this.unfilteredallAvailableChannels = [];
        // Changing the location resets the search — otherwise the stale search text stays visible
        // while the rebuilt markers ignore it, leaving the box and results out of sync.
        this.clearSearchInput();
        this.selectedCountry = country.value;
        // Any country change clears the city selection — deliberately not restored when the
        // user switches back to the previously selected country.
        this.clearCitySelection();

        // Prevent selection of disabled countries for CPM billing method
        if (this.isCountryDisabled(this.selectedCountry)) {
            this.selectedCountry = {};
            this.form.get('country').setValue(null);
            this.markers = [];
            return;
        }

        // No city is selected right after a country change, so every channel in the country is
        // shown — the user can browse and select them before narrowing down (see applyCityFilter).
        this.applyCityFilter();
    }

    /**
     * Updates the control (so the City field shows the comma-separated selection live) but does NOT
     * filter the map — the filter is applied only when the dropdown closes (see onCityPanelToggled).
     * Strips the "All" sentinel the Select All row toggles into the control.
     */
    onCitySelectionChange(event: any): void {
        const value: any[] = event.value || [];
        const cities = value.filter((v: any) => v !== 'All');
        if (cities.length !== value.length) {
            this.form.get('city').setValue(cities);
        }
    }

    /** Apply the city filter only when the dropdown closes (click-away), not on each selection. */
    onCityPanelToggled(opened: boolean): void {
        if (!opened) {
            this.commitCitySelection();
        }
    }

    areAllCitiesSelected(): boolean {
        const selected = (this.f.city.value || []).filter((v: any) => v !== 'All');
        const cities = this.selectedCountry?.cities || [];
        return cities.length > 0 && selected.length === cities.length;
    }

    onToggleAllCities(): void {
        const cities = this.selectedCountry?.cities || [];
        this.form.get('city').setValue(this.areAllCitiesSelected() ? [] : [...cities]);
        // Filtering is deferred until the dropdown closes — see onCityPanelToggled.
    }

    /** Sync {@link selectedCities} with the control and rebuild markers if the selection changed. */
    private commitCitySelection(): void {
        const cities: any[] =
            (this.form.get('city').value || []).filter((v: any) => v !== 'All');
        const changed =
            cities.length !== this.selectedCities.length ||
            cities.some(city => !this.selectedCities.includes(city));
        if (!changed) return;
        this.selectedCities = cities;
        // Genuine city change resets the search (see onChangeCountry).
        this.clearSearchInput();
        this.applyCityFilter();
    }

    private clearCitySelection(): void {
        this.selectedCities = [];
        this.form.get('city').setValue([]);
    }

    /**
     * Rebuild markers for the cities on display + indoor/outdoor filter. Also the fallback when
     * the search box is cleared.
     */
    private applyCityFilter(): void {
        const cities = this.getCitiesToDisplay();
        if (cities.length > 0) {
            this.rebuildMarkers(cities, (channel: any) => this.matchesIndoorOutdoor(channel));
        } else {
            this.markers = [];
            this.fitMapToMarkers();
        }
    }

    /**
     * Cities whose channels are shown: the selected ones, or — when a country is chosen but no
     * city yet — every city in that country, so the country's channels can be browsed and
     * selected without picking a city first.
     */
    private getCitiesToDisplay(): any[] {
        const selected = this.getSelectedCitiesInCountry();
        if (selected.length > 0) return selected;
        return this.selectedCountry?.cities || [];
    }

    /** Selected cities that belong to the current country — guards against stale selections. */
    private getSelectedCitiesInCountry(): any[] {
        const countryCities = this.selectedCountry?.cities || [];
        return (this.selectedCities || []).filter((city: any) =>
            countryCities.some((c: any) => c.idcity === city.idcity));
    }

    /** Clear the (unbound) search input so it can't drift out of sync with the location filters. */
    private clearSearchInput(): void {
        const input = document.getElementById('tosearchfor') as HTMLInputElement;
        if (input) input.value = '';
    }

    onIndoorOutdoorChecked() {
        // If a search is active, re-run it so the indoor/outdoor change is reflected in results.
        const searchText = (document.getElementById('tosearchfor') as HTMLInputElement)?.value;
        if (searchText && searchText.length > 0) {
            this.filterChange();
            return;
        }
        if (this.selectedCountry?.idcountry != null) {
            this.applyCityFilter();
        }
    }

    /** Common predicate for the indoor/outdoor checkboxes — both off means "no filter". */
    private matchesIndoorOutdoor(channel): boolean {
        const indoor = this.f.indoor.value === true;
        const outdoor = this.f.outdoor.value === true;
        if (!indoor && !outdoor) return true;
        if (indoor && channel.type === 'INDOOR') return true;
        if (outdoor && channel.type === 'OUTDOOR') return true;
        return false;
    }

    /**
     * Reset markers from a list of cities, pushing one marker per channel that passes `predicate`.
     * Consolidates the loop pattern shared by onChangeCountry/applyCityFilter/filterChange.
     */
    private rebuildMarkers(cities: any[], predicate: (channel: any) => boolean): void {
        this.unfilteredallAvailableChannels = [];
        this.markers = [];
        if (!cities) return;
        for (const city of cities) {
            if (!city || !city.zones) continue;
            for (const zone of city.zones) {
                for (const channel of (zone.channels || [])) {
                    if (predicate(channel)) {
                        this.markers.push({
                            lat: channel.lat,
                            lng: channel.lon,
                            label: channel.name,
                            options: channel,
                            draggable: false
                        });
                    }
                }
            }
        }
        this.fitMapToMarkers();
    }

    /** Inverse of {@link isBlockedUnderCpm}: a country is selectable unless CPM excludes it. */
    private isCountryAllowedForBilling(country: any): boolean {
        return !this.isBlockedUnderCpm(country);
    }

    getIconUrl(marker) {
        if (this.selectedChannels.has(marker.options.idChannel)) {
            return './assets/images/icons/common/screen-selected.png';
        }
        return './assets/images/icons/common/screen-light.png';
    }

    private isReadOnlyFlow(): boolean {
        return this.previewMode || !this.authenticationService.isClient();
    }

    async navigateNextStep() {
        this.alertService.clear();
        if (this.pendingOps$.value > 0) {
            this.loadingOverlayService.show();
        }
        await this.waitForPendingOps();
        this.loadingOverlayService.hide();

        // Linked channels must be booked together — block proceeding while siblings are missing.
        const missingLinked = this.getMissingLinkedSiblings();
        if (missingLinked.length > 0) {
            this.alertService.error(
                'Linked channels must be booked together. Please also add: ' + missingLinked.join(', '),
                { id: 'save-campaign', autoClose: true }
            );
            return;
        }

        const url =
            '/campaign/step-3/' +
            this.campaign._id +
            (this.partialEditMode ? `?partial-edit-mode=true` : '');
        this.router.navigateByUrl(url);
    }

    /** Main image of a channel: `image`, else the first of `images`; null when it has none. */
    getChannelMainImage(channel): string | null {
        return channel?.image || channel?.images?.[0] || null;
    }

    /** Opens the channel's image viewer in a new tab without touching the Step 2 selection. */
    openChannelImages(channelId, event?: Event) {
        event?.preventDefault();
        event?.stopPropagation();
        const url = this.router.serializeUrl(this.router.createUrlTree(['/channel-images', channelId]));
        window.open(url, '_blank', 'noopener');
    }

    getTotalScreens(groups) {
        return groups.reduce((total, obj) => (obj.screens || 0) + total, 0);
    }

    getTotalAvgImpressionsPerHour(avg) {
        return avg.reduce((total, obj) => (obj.avgImpressionPerHour || 0) * (obj.screens || 0) + total, 0);
    }

    /**
     * Builds the legacy `groups` array the detail card/template expects from the post-restructure
     * channel model. Dimension groups become one entry each (length > 1 → "Many" in the UI);
     * otherwise a single synthetic entry carries the channel's uniform dimensions. The synthetic
     * entry's per-screen avg is divided by the screen count so getTotalAvgImpressionsPerHour
     * reconstructs the channel's stored total exactly.
     */
    private buildChannelGroups(channel: any): any[] {
        if (channel?.channelScreens && channel.channelScreens.length > 0) {
            // How many screens each dimension group covers isn't recorded — the admin enters the
            // totals only — so spread them evenly (remainder to the first groups) and keep the
            // channel's screen count and impression total intact. Mirrors ChannelDTO.getGroups().
            const groupCount = channel.channelScreens.length;
            const screenCount = channel?.screens > groupCount ? channel.screens : groupCount;
            const perGroup = Math.floor(screenCount / groupCount);
            const remainder = screenCount % groupCount;
            return channel.channelScreens.map((s: any, i: number) => ({
                idGroup: s.id,
                width: s.width,
                height: s.height,
                dpi: s.dpi,
                screenSize: s.screenSize,
                loopDuration: s.loopDuration,
                nbrSlotsPerLoop: s.nbrSlotsPerLoop,
                avgImpressionPerHour: s.avgImpressionPerHour,
                screens: perGroup + (i < remainder ? 1 : 0)
            }));
        }
        const screens = channel?.screens || 1;
        return [{
            idGroup: channel?.idChannel,
            width: channel?.width,
            height: channel?.height,
            dpi: channel?.dpi,
            screenSize: channel?.screenSize,
            loopDuration: channel?.loopDuration,
            nbrSlotsPerLoop: channel?.nbrSlotsPerLoop,
            avgImpressionPerHour: channel?.avgImpressionPerHour != null
                ? channel.avgImpressionPerHour / screens
                : null,
            screens: screens
        }];
    }

    public showMap: boolean = true;
    public mapBtnTxt: any = 'Select from List';
    public showSelected = true;
    public showSelectedBtnTxt = 'Hide Selected';

    toggledisplaySelected() {
        this.showSelected = !this.showSelected;
        this.showSelectedBtnTxt = this.showSelected ? 'Hide Selected' : 'Show Selected';
    }

    toggledisplaymap() {
        this.showMap = !this.showMap;
        if (this.showMap) this.mapBtnTxt = 'Select from List';
        else {
            this.mapBtnTxt = 'Select from Map';
            if (this.unfilteredallAvailableChannels.length == 0) {
                this.unfilteredallAvailableChannels = this.markers;
            } else {
                this.markers = this.unfilteredallAvailableChannels;
            }
        }
    }

    locateSelectedOnMap(countryIN, cityIN, channelIN) {
        if (!this.showMap) this.toggledisplaymap();

        const countryMatch = this.locations.countries.find((c: any) =>
            c.idcountry == countryIN && this.isCountryAllowedForBilling(c));
        const cityMatch = countryMatch?.cities.find((c: any) => c.idcity == cityIN);

        this.rebuildMarkers(cityMatch ? [cityMatch] : [],
            (channel: any) => channel.idChannel == channelIN);

        if (this.markers.length == 1) this.isSnazzyInfoWindowOpened = true;
    }

    filterChange() {
        const textIn = (document.getElementById('tosearchfor') as HTMLInputElement).value;
        if (textIn.length === 0) {
            // Search cleared: fall back to the current country/city selection rather than blanking out.
            this.applyCityFilter();
            return;
        }

        const textInWords = textIn.toLocaleLowerCase().split(' ');

        // Scope the search to the currently selected country so a channel name that exists
        // in several countries only surfaces matches for the chosen one. When no country is
        // selected, fall back to searching across all billing-allowed countries.
        const selectedCountryId = this.selectedCountry?.idcountry;
        const countriesToSearch = selectedCountryId != null
            ? this.locations.countries.filter((c: any) => c.idcountry === selectedCountryId)
            : this.locations.countries;

        const allCities = countriesToSearch
            .filter((c: any) => this.isCountryAllowedForBilling(c))
            .flatMap((c: any) => c.cities);

        // Further narrow to the selected cities when any are chosen. getSelectedCitiesInCountry
        // guards that they belong to the selected country so a stale selection left over from a
        // previous country doesn't wipe out all results (falls back to the whole country then).
        const selectedCityIds = new Set(
            this.getSelectedCitiesInCountry().map((c: any) => c.idcity));
        const citiesToSearch = selectedCityIds.size > 0
            ? allCities.filter((c: any) => selectedCityIds.has(c.idcity))
            : allCities;

        // Match on name AND honour the indoor/outdoor checkboxes, so search results respect the
        // same filters as the country/city dropdowns.
        this.rebuildMarkers(citiesToSearch, (channel: any) => {
            const name = (channel.name || '').toLowerCase();
            const nameMatches = textInWords.every(word => name.includes(word));
            return nameMatches && this.matchesIndoorOutdoor(channel);
        });
    }

    onMapReady(map: google.maps.Map) {
        this.map = map;
        this.fitMapToMarkers();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['markers'] && !changes['markers'].firstChange) {
            this.fitMapToMarkers();
        }
    }

    fitMapToMarkers() {
        if (!this.map || !this.markers || this.markers.length === 0) return;

        const bounds = new google.maps.LatLngBounds();
        for (const marker of this.markers) {
            bounds.extend(new google.maps.LatLng(marker.lat, marker.lng));
        }

        this.map.fitBounds(bounds);

        const offsetDistance = 0.00001;

        this.markers = this.markers.map((marker, index) => ({
            ...marker,
            lat: marker.lat + index * offsetDistance,
            lng: marker.lng + index * offsetDistance
        }));

        google.maps.event.addListenerOnce(this.map, 'idle', () => {
            const calculatedZoom = calculateZoomForBounds(this.map, bounds);
            this.map.setZoom(clampZoomForMarkerCount(calculatedZoom, this.markers.length));
        });
    }

    getMarkerPricingMethod() {
        return this.campaign.billingMethod === 'PER_HOUR' ? 'Price per hour' : 'CPM';
    }

    getMarkerPricing(channel: any) {
        return this.campaign.billingMethod === 'PER_HOUR' ? channel.cph : channel.peakCPM;
    }

    showSectionPerBillingMethod(channel: any) {
        return this.campaign.billingMethod === 'PER_HOUR';
    }

    /** True when CPM billing is active and this country is not one CPM can be booked in. */
    private isBlockedUnderCpm(country: any): boolean {
        return isCountryBlockedForBilling(country, this.campaign?.billingMethod);
    }

    // A country outside CPM_COUNTRY_ISO3 can't be selected (and gets a "(CPH model only)" hint)
    // under CPM billing.
    isCountryDisabled(country: any): boolean {
        return this.isBlockedUnderCpm(country);
    }

    getCountryDisplaySuffix(country: any): string {
        return this.isBlockedUnderCpm(country) ? ' (CPH model only)' : '';
    }
}
